import { useT } from "../lib/i18n";
import type { CheckIn } from "@shared/types";
import { weeklyIntakeVerdict } from "@shared/intake-verdict";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

type Props = {
  checkIn: CheckIn;
  /** Current daily calorie target — enables the intake verdict line. Omit (or 0)
   *  to hide it (e.g. in the read-only history, where the period's target is
   *  unknown). */
  target?: number;
  readOnly?: boolean;
  onApply: () => void;
  onDismiss: () => void;
  onClose: () => void;
};

export function CheckInSheet({ checkIn, target = 0, readOnly = false, onApply, onDismiss, onClose }: Props) {
  const t = useT();
  if (!checkIn) return null;

  const delta = checkIn.suggestion?.delta_kcal ?? 0;
  const msg = checkIn.suggestion?.message ?? "";

  const verdict = weeklyIntakeVerdict({
    avgCalories: checkIn.avg_calories,
    target,
    daysWithFood: checkIn.days_with_food ?? 0,
  });

  return (
    <SheetShell onClose={onClose}>
      <div className="space-y-5 pt-2">
        <h2 className="text-lg font-semibold">{t("check_in.title", { week: checkIn.week_starting })}</h2>

        <div className="grid grid-cols-3 gap-3">
          <Stat
            label={t("check_in.avg_weight")}
            value={checkIn.avg_weight_kg !== null ? `${checkIn.avg_weight_kg.toFixed(1)} kg` : "—"}
          />
          <Stat
            label={t("check_in.weight_delta")}
            value={
              checkIn.weight_delta_kg !== null
                ? `${checkIn.weight_delta_kg > 0 ? "+" : ""}${checkIn.weight_delta_kg.toFixed(1)} kg`
                : "—"
            }
          />
          <Stat label={t("check_in.adherence")} value={`${checkIn.adherence_pct}%`} />
        </div>

        {verdict.status !== "not_enough_data" && (
          <Card title={t("check_in.intake_title")}>
            <div className="text-sm text-fg leading-relaxed">
              {t(`check_in.verdict.${verdict.status}`, {
                avg: checkIn.avg_calories,
                target,
                nudge: Math.abs(verdict.suggested_delta_kcal),
              })}
            </div>
          </Card>
        )}

        <Card title={t("check_in.suggestion")}>
          <div className="text-sm text-fg leading-relaxed">{msg}</div>
        </Card>

        {!readOnly && checkIn.user_action === null && (
          <div className="flex gap-2">
            <Button onClick={onApply} className="flex-1">
              {delta !== 0
                ? t("check_in.apply", { delta: delta > 0 ? `+${delta}` : String(delta) })
                : t("check_in.apply_no_delta")}
            </Button>
            <Button variant="secondary" onClick={onDismiss}>
              {t("check_in.dismiss")}
            </Button>
          </div>
        )}

        {checkIn.user_action !== null && (
          <div
            className={`text-center text-sm ${
              checkIn.user_action === "applied" ? "text-success" : "text-fg-muted"
            }`}
          >
            {checkIn.user_action === "applied"
              ? t("check_in.applied_badge")
              : t("check_in.dismissed_badge")}
          </div>
        )}
      </div>
    </SheetShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface rounded-2xl p-3 text-center">
      <div className="text-sm text-fg-faint uppercase tracking-wider">{label}</div>
      <div className="text-base tabular-nums mt-1">{value}</div>
    </div>
  );
}
